import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectAPI, assetAPI, teamDirectoryAPI, reviewAPI } from '../services/api';
import { COGNITIVE_LANES, COGNITIVE_LEVELS } from '../mock';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import AssetsTab from '../components/AssetsTab';
import WorkstreamsTab from '../components/WorkstreamsTab';
import TaskPhasesSection, { PhaseProgressBadge } from '../components/TaskPhasesSection';
import { 
  ArrowLeft, TrendingUp, AlertTriangle, Calendar, Users, 
  CheckCircle2, Clock, Target, ListTodo, X, ChevronRight,
  Folder, FileText, Layers, ClipboardCheck, RotateCcw, UserCheck
} from 'lucide-react';

// Team Strip Component - shows overlapping avatars
const TeamStrip = ({ owner, contributors, size = 'sm', maxShow = 4 }) => {
  const avatarSize = size === 'lg' ? 'w-10 h-10 text-sm' : 'w-6 h-6 text-xs';
  const ownerSize = size === 'lg' ? 'w-12 h-12 text-base' : 'w-8 h-8 text-xs';
  const overlap = size === 'lg' ? '-ml-3' : '-ml-2';
  
  const displayContributors = contributors?.slice(0, maxShow) || [];
  const extraCount = (contributors?.length || 0) - maxShow;

  return (
    <div className="flex items-center" data-testid="team-strip">
      {/* Project Manager */}
      {owner && (
        <div className="relative group" title={`PM: ${owner.name}`}>
          <div 
            className={`${ownerSize} rounded-full flex items-center justify-center font-medium ring-2 ring-white shadow-sm`}
            style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
          >
            {owner.avatar}
          </div>
          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-amber-400 flex items-center justify-center text-white shadow-sm" style={{ fontSize: '8px' }}>
            PM
          </div>
        </div>
      )}
      
      {/* Contributors */}
      {displayContributors.map((contributor, idx) => (
        <div 
          key={contributor.id} 
          className={`relative ${overlap}`}
          title={contributor.name}
        >
          <div 
            className={`${avatarSize} rounded-full flex items-center justify-center font-medium ring-2 ring-white shadow-sm`}
            style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
          >
            {contributor.avatar}
          </div>
        </div>
      ))}
      
      {/* Extra count */}
      {extraCount > 0 && (
        <div 
          className={`${avatarSize} rounded-full flex items-center justify-center font-medium ring-2 ring-white shadow-sm ${overlap}`}
          style={{ backgroundColor: '#E5E7EB', color: '#6B7280' }}
        >
          +{extraCount}
        </div>
      )}
    </div>
  );
};

// Task Detail Panel
const TaskDetailPanel = ({ task, onClose }) => {
  const navigate = useNavigate();
  const lane = COGNITIVE_LANES.find(l => l.id === task?.primary_lane);
  const level = COGNITIVE_LEVELS.find(l => l.id === task?.required_level);
  
  const statusColors = {
    'Planned': { bg: '#F3F4F6', text: '#6B7280' },
    'Active': { bg: '#DBEAFE', text: '#1E40AF' },
    'In Review': { bg: '#FEF3C7', text: '#92400E' },
    'Completed': { bg: '#D1FAE5', text: '#059669' }
  };
  const statusColor = statusColors[task?.status] || statusColors.Planned;

  if (!task) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="task-detail-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-start justify-between" style={{ borderColor: '#E9ECEF' }}>
          <div className="flex-1 pr-4">
            <h2 className="text-lg font-semibold" style={{ color: '#264653' }}>{task.title}</h2>
            <span 
              className="inline-block mt-2 text-xs px-2 py-1 rounded-full"
              style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
            >
              {task.status}
            </span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" style={{ color: '#6B7280' }} />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Description</h3>
            <p className="text-sm" style={{ color: '#374151' }}>{task.description || 'No description'}</p>
          </div>
          
          {/* Assignee */}
          <div>
            <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Assignee</h3>
            {task.assignee ? (
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                  style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                >
                  {task.assignee.avatar}
                </div>
                <span className="text-sm font-medium" style={{ color: '#264653' }}>{task.assignee.name}</span>
              </div>
            ) : (
              <p className="text-sm" style={{ color: '#9CA3AF' }}>Unassigned</p>
            )}
          </div>
          
          {/* Project Link */}
          {task.project_id && (
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Project</h3>
              <button 
                onClick={() => { onClose(); navigate(`/projects/${task.project_id}`); }}
                className="flex items-center gap-2 text-sm hover:underline"
                style={{ color: '#2A9D8F' }}
              >
                <Target className="w-4 h-4" />
                View Project
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
          
          {/* Lane & Level */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Lane</h3>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lane?.color || '#6B7280' }} />
                <span className="text-sm" style={{ color: '#374151' }}>{lane?.name || task.primary_lane}</span>
              </div>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Level</h3>
              <span className="text-sm font-medium" style={{ color: '#264653' }}>{level?.id || task.required_level}</span>
            </div>
          </div>
          
          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Due Date</h3>
              <div className="flex items-center gap-2 text-sm" style={{ color: '#374151' }}>
                <Calendar className="w-4 h-4" style={{ color: '#6B7280' }} />
                {task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Not set'}
              </div>
            </div>
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Created</h3>
              <div className="flex items-center gap-2 text-sm" style={{ color: '#374151' }}>
                <Clock className="w-4 h-4" style={{ color: '#6B7280' }} />
                {task.created_at ? new Date(task.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Unknown'}
              </div>
            </div>
          </div>
          
          {/* Definition of Success */}
          {task.definition_of_success && (
            <div>
              <h3 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Definition of Success</h3>
              <p className="text-sm" style={{ color: '#374151' }}>{task.definition_of_success}</p>
            </div>
          )}
          
          {/* Task Phases Section */}
          <div className="border-t pt-4" style={{ borderColor: '#E9ECEF' }}>
            <TaskPhasesSection taskId={task.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Main ProjectDetail Component
const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assetSummary, setAssetSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [reviewHistory, setReviewHistory] = useState([]);
  const [reviewInfo, setReviewInfo] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const [projectRes, assetRes, teamRes, reviewHistoryRes, reviewInfoRes] = await Promise.all([
          projectAPI.getOne(projectId),
          assetAPI.getProjectAssetSummary(projectId).catch(() => ({ data: null })),
          teamDirectoryAPI.getAll().catch(() => ({ data: { members: [] } })),
          reviewAPI.getProjectReviewHistory(projectId).catch(() => ({ data: { history: [] } })),
          reviewAPI.getProjectReviewInfo(projectId).catch(() => ({ data: null }))
        ]);
        setProject(projectRes.data.project);
        setTasks(projectRes.data.tasks || []);
        setAssetSummary(assetRes.data);
        setTeamMembers(teamRes.data.members || []);
        setReviewHistory(reviewHistoryRes.data?.history || []);
        setReviewInfo(reviewInfoRes.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [projectId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center" style={{ minHeight: '400px' }}>
        <p style={{ color: '#8D99AE' }}>Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-7xl mx-auto flex items-center justify-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" style={{ color: '#E76F51' }} />
          <p style={{ color: '#E76F51' }}>Project not found</p>
          <Button onClick={() => navigate('/projects')} className="mt-4" variant="outline">
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  const completedTasks = tasks.filter(t => t.status === 'Completed').length;
  const activeTasks = tasks.filter(t => t.status === 'Active' || t.status === 'Planned').length;
  const overdueTasks = tasks.filter(t => {
    if (!t.due_date || t.status === 'Completed') return false;
    return new Date(t.due_date) < new Date();
  }).length;

  const statusColors = {
    'Planned': { bg: '#F3F4F6', text: '#6B7280' },
    'Active': { bg: '#DBEAFE', text: '#1E40AF' },
    'In Review': { bg: '#FEF3C7', text: '#92400E' },
    'Completed': { bg: '#D1FAE5', text: '#059669' }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="project-detail">
      {/* Back Button */}
      <button 
        onClick={() => navigate('/projects')}
        className="flex items-center gap-2 text-sm hover:underline"
        style={{ color: '#6B7280' }}
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Projects
      </button>

      {/* Project Header */}
      <div className="bg-white rounded-xl shadow-sm border p-6" style={{ borderColor: '#E9ECEF' }}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-semibold" style={{ color: '#264653' }}>{project.name}</h1>
              {project.status === 'on-track' ? (
                <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full" style={{ backgroundColor: '#D1FAE5', color: '#059669' }}>
                  <TrendingUp className="w-3 h-3" />
                  On Track
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs px-3 py-1 rounded-full" style={{ backgroundColor: '#FED7AA', color: '#EA580C' }}>
                  <AlertTriangle className="w-3 h-3" />
                  Focus Required
                </span>
              )}
              {project.health_score && (
                <span 
                  className="text-xs px-3 py-1 rounded-full font-medium"
                  style={{ 
                    backgroundColor: project.health_level === 'healthy' ? '#D1FAE5' : project.health_level === 'stable' ? '#DBEAFE' : '#FEF3C7',
                    color: project.health_level === 'healthy' ? '#059669' : project.health_level === 'stable' ? '#1E40AF' : '#92400E'
                  }}
                >
                  Health: {Math.round(project.health_score)}
                </span>
              )}
            </div>
            <p className="text-sm mb-4" style={{ color: '#8D99AE' }}>{project.description}</p>
            
            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs" style={{ color: '#6B7280' }}>Progress</span>
                <span className="text-xs font-medium" style={{ color: '#264653' }}>{project.completion_percentage}%</span>
              </div>
              <Progress value={project.completion_percentage} className="h-2" />
            </div>
          </div>
          
          {/* Team Strip (larger) */}
          <div className="flex flex-col items-end">
            <TeamStrip 
              owner={project.owner} 
              contributors={project.team?.filter(t => !t.is_owner) || []} 
              size="lg"
              maxShow={5}
            />
            <p className="text-xs mt-2" style={{ color: '#6B7280' }}>
              {project.team?.length || 0} team members
            </p>
          </div>
        </div>
        
        {/* Stats Row */}
        <div className="grid grid-cols-6 gap-4 pt-4 border-t" style={{ borderColor: '#E9ECEF' }}>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#264653' }}>{tasks.length}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Total Tasks</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#2A9D8F' }}>{completedTasks}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#3B82F6' }}>{activeTasks}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Active</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: overdueTasks > 0 ? '#E76F51' : '#264653' }}>{overdueTasks}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Overdue</p>
          </div>
          <div className="text-center border-l" style={{ borderColor: '#E9ECEF' }}>
            <p className="text-2xl font-semibold" style={{ color: '#6366F1' }}>{assetSummary?.total_assets || 0}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Assets</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold" style={{ color: '#F59E0B' }}>{assetSummary?.total_deliverables || 0}</p>
            <p className="text-xs" style={{ color: '#6B7280' }}>Deliverables</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Target className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex items-center gap-2">
            <ListTodo className="w-4 h-4" />
            Tasks
            <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100">{tasks.length}</span>
          </TabsTrigger>
          <TabsTrigger value="workstreams" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Workstreams
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Team
          </TabsTrigger>
          <TabsTrigger value="assets" className="flex items-center gap-2">
            <Folder className="w-4 h-4" />
            Assets & Deliverables
            {assetSummary?.total_assets > 0 && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100">{assetSummary.total_assets}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="review-history" className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" />
            Review History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: '#264653' }}>Key Metrics</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: '#6B7280' }}>On-Time Rate</span>
                  <span className="text-sm font-medium" style={{ color: '#264653' }}>{Math.round((project.on_time_rate || 0) * 100)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: '#6B7280' }}>Overdue Steps</span>
                  <span className="text-sm font-medium" style={{ color: project.overdue_steps > 0 ? '#E76F51' : '#264653' }}>{project.overdue_steps || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm" style={{ color: '#6B7280' }}>Overdue Phases</span>
                  <span className="text-sm font-medium" style={{ color: project.overdue_phases > 0 ? '#E76F51' : '#264653' }}>{project.overdue_phases || 0}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
              <h3 className="text-sm font-medium mb-3" style={{ color: '#264653' }}>Project Manager</h3>
              {project.owner ? (
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center text-base font-medium"
                    style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                  >
                    {project.owner.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#264653' }}>{project.owner.name}</p>
                    {project.owner.title && (
                      <p className="text-xs" style={{ color: '#6B7280' }}>{project.owner.title}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-sm" style={{ color: '#9CA3AF' }}>No project manager assigned</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <div className="bg-white rounded-lg shadow-sm border" style={{ borderColor: '#E9ECEF' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b" style={{ borderColor: '#E9ECEF', backgroundColor: '#F9FAFB' }}>
                    <th className="text-left p-4 text-xs font-medium" style={{ color: '#6B7280' }}>Task</th>
                    <th className="text-left p-4 text-xs font-medium" style={{ color: '#6B7280' }}>Assignee</th>
                    <th className="text-left p-4 text-xs font-medium" style={{ color: '#6B7280' }}>Lane</th>
                    <th className="text-left p-4 text-xs font-medium" style={{ color: '#6B7280' }}>Phases</th>
                    <th className="text-left p-4 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
                    <th className="text-left p-4 text-xs font-medium" style={{ color: '#6B7280' }}>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => {
                    const lane = COGNITIVE_LANES.find(l => l.id === task.primary_lane);
                    const level = COGNITIVE_LEVELS.find(l => l.id === task.required_level);
                    const statusColor = statusColors[task.status];

                    return (
                      <tr 
                        key={task.id} 
                        onClick={() => setSelectedTask(task)}
                        className="border-b hover:bg-gray-50 cursor-pointer transition-colors" 
                        style={{ borderColor: '#E9ECEF' }}
                        data-testid={`task-row-${task.id}`}
                      >
                        <td className="p-4">
                          <p className="text-sm font-medium" style={{ color: '#264653' }}>{task.title}</p>
                        </td>
                        <td className="p-4">
                          {task.assignee ? (
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                                style={{ backgroundColor: '#E8F4F2', color: '#2A9D8F' }}
                              >
                                {task.assignee.avatar}
                              </div>
                              <span className="text-sm" style={{ color: '#6B7280' }}>{task.assignee.name}</span>
                            </div>
                          ) : (
                            <span className="text-sm" style={{ color: '#9CA3AF' }}>Unassigned</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: lane?.color || '#6B7280' }} />
                            <span className="text-sm" style={{ color: '#6B7280' }}>{lane?.name || task.primary_lane}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          {task.phase_count > 0 ? (
                            <PhaseProgressBadge 
                              phaseCount={task.phase_count} 
                              completedPhaseCount={task.completed_phase_count || 0}
                              hasOverdue={task.has_overdue_phase}
                            />
                          ) : (
                            <span className="text-xs" style={{ color: '#9CA3AF' }}>—</span>
                          )}
                        </td>
                        <td className="p-4">
                          <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: statusColor.bg, color: statusColor.text }}>
                            {task.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className="text-sm" style={{ color: '#6B7280' }}>
                            {task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team" className="space-y-4">
          <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E9ECEF' }}>
            <h3 className="text-sm font-medium mb-4" style={{ color: '#264653' }}>Project Team ({project.team?.length || 0} members)</h3>
            <div className="space-y-3">
              {project.team?.map((member) => (
                <div 
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${member.is_owner ? 'border-l-4' : ''}`}
                  style={{ 
                    backgroundColor: member.is_owner ? '#FEF3C7' : '#F9FAFB',
                    borderColor: member.is_owner ? '#F59E0B' : 'transparent'
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium"
                      style={{ backgroundColor: member.is_owner ? '#FDE68A' : '#E5E7EB', color: member.is_owner ? '#92400E' : '#374151' }}
                    >
                      {member.avatar}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium" style={{ color: '#264653' }}>{member.name}</p>
                        {member.is_owner && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-400 text-white">PM</span>
                        )}
                      </div>
                      {member.title && (
                        <p className="text-xs" style={{ color: '#6B7280' }}>{member.title}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold" style={{ color: '#264653' }}>{member.task_count}</p>
                    <p className="text-xs" style={{ color: '#6B7280' }}>tasks</p>
                  </div>
                </div>
              ))}
              {(!project.team || project.team.length === 0) && (
                <p className="text-sm text-center py-4" style={{ color: '#9CA3AF' }}>No team members assigned</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Workstreams Tab */}
        <TabsContent value="workstreams" className="space-y-4">
          <WorkstreamsTab projectId={projectId} tasks={tasks} teamMembers={teamMembers} />
        </TabsContent>

        {/* Assets & Deliverables Tab */}
        <TabsContent value="assets" className="space-y-4">
          <AssetsTab projectId={projectId} tasks={tasks} />
        </TabsContent>
        
        {/* Review History Tab */}
        <TabsContent value="review-history" className="space-y-4" data-testid="review-history-tab">
          {/* Current Review Status */}
          {reviewInfo && (
            <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E9ECEF' }}>
              <h3 className="text-sm font-medium mb-4" style={{ color: '#264653' }}>Current Review Status</h3>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span 
                      className="text-sm px-3 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: 
                          reviewInfo.review_status === 'approved' ? '#D1FAE5' :
                          reviewInfo.review_status === 'pending' ? '#FEF3C7' :
                          reviewInfo.review_status === 'revisions_requested' ? '#FED7AA' : '#F3F4F6',
                        color:
                          reviewInfo.review_status === 'approved' ? '#059669' :
                          reviewInfo.review_status === 'pending' ? '#D97706' :
                          reviewInfo.review_status === 'revisions_requested' ? '#EA580C' : '#6B7280'
                      }}
                    >
                      {reviewInfo.review_status === 'approved' ? 'Approved' :
                       reviewInfo.review_status === 'pending' ? 'Awaiting Review' :
                       reviewInfo.review_status === 'revisions_requested' ? 'Revisions Requested' :
                       reviewInfo.review_status === 'none' ? 'Not Submitted' : 
                       reviewInfo.review_status}
                    </span>
                    {reviewInfo.sla_status && (
                      <span 
                        className="text-xs px-2 py-1 rounded-full"
                        style={{
                          backgroundColor: 
                            reviewInfo.sla_status === 'green' ? '#D1FAE5' :
                            reviewInfo.sla_status === 'amber' ? '#FEF3C7' : '#FEE2E2',
                          color:
                            reviewInfo.sla_status === 'green' ? '#059669' :
                            reviewInfo.sla_status === 'amber' ? '#D97706' : '#DC2626'
                        }}
                      >
                        {reviewInfo.hours_pending?.toFixed(1)}h pending
                      </span>
                    )}
                  </div>
                  {reviewInfo.reviewer_name && (
                    <p className="text-sm" style={{ color: '#6B7280' }}>
                      Reviewer: <span className="font-medium" style={{ color: '#264653' }}>{reviewInfo.reviewer_name}</span>
                    </p>
                  )}
                  {reviewInfo.review_comment && (
                    <div className="mt-3 p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                      <p className="text-sm" style={{ color: '#374151' }}>"{reviewInfo.review_comment}"</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Review History Timeline */}
          <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E9ECEF' }}>
            <h3 className="text-sm font-medium mb-4" style={{ color: '#264653' }}>Review History</h3>
            
            {reviewHistory.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardCheck className="w-12 h-12 mx-auto mb-3" style={{ color: '#E5E7EB' }} />
                <p className="text-sm" style={{ color: '#9CA3AF' }}>
                  No review history for this project yet
                </p>
                <p className="text-xs mt-1" style={{ color: '#D1D5DB' }}>
                  Submit the project for executive review to start tracking
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviewHistory.map((entry, idx) => {
                  const getActionConfig = (action) => {
                    switch (action) {
                      case 'submitted':
                        return { icon: ClipboardCheck, color: '#6366F1', bg: '#EEF2FF', label: 'Submitted for Review' };
                      case 'approved':
                        return { icon: CheckCircle2, color: '#059669', bg: '#D1FAE5', label: 'Approved' };
                      case 'revisions_requested':
                        return { icon: RotateCcw, color: '#D97706', bg: '#FEF3C7', label: 'Revisions Requested' };
                      case 'reassigned':
                        return { icon: UserCheck, color: '#8B5CF6', bg: '#EDE9FE', label: 'Reviewer Reassigned' };
                      default:
                        return { icon: Clock, color: '#6B7280', bg: '#F3F4F6', label: action };
                    }
                  };
                  
                  const config = getActionConfig(entry.action);
                  const Icon = config.icon;
                  const timestamp = entry.timestamp ? new Date(entry.timestamp) : null;
                  
                  return (
                    <div 
                      key={entry.id || idx}
                      className="flex items-start gap-4"
                      data-testid={`review-history-entry-${entry.id || idx}`}
                    >
                      {/* Timeline connector */}
                      <div className="flex flex-col items-center">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: config.bg }}
                        >
                          <Icon className="w-5 h-5" style={{ color: config.color }} />
                        </div>
                        {idx < reviewHistory.length - 1 && (
                          <div className="w-0.5 h-full min-h-[24px] mt-2" style={{ backgroundColor: '#E5E7EB' }} />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 pb-4">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h4 className="text-sm font-medium" style={{ color: '#264653' }}>
                            {config.label}
                          </h4>
                          <span className="text-xs" style={{ color: '#9CA3AF' }}>
                            {timestamp ? timestamp.toLocaleString() : 'Unknown date'}
                          </span>
                        </div>
                        <p className="text-sm mb-1" style={{ color: '#6B7280' }}>
                          by <span className="font-medium">{entry.performed_by_name || 'Unknown'}</span>
                        </p>
                        {entry.comment && (
                          <div className="mt-2 p-3 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                            <p className="text-sm" style={{ color: '#374151' }}>"{entry.comment}"</p>
                          </div>
                        )}
                        {entry.new_reviewer_id && (
                          <p className="text-xs mt-2" style={{ color: '#8B5CF6' }}>
                            New reviewer assigned
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Task Detail Panel */}
      {selectedTask && (
        <TaskDetailPanel task={selectedTask} onClose={() => setSelectedTask(null)} />
      )}
    </div>
  );
};

export default ProjectDetail;
